import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api } from "../lib/api";
import { getSessionId } from "../lib/session";

const CATEGORIES = [
    { value: "bug", label: "Bug" },
    { value: "feedback", label: "Feedback" },
    { value: "feature", label: "Idea" },
];

export default function FeedbackWidget() {
    const [open, setOpen] = useState(false);
    const [category, setCategory] = useState("feedback");
    const [message, setMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e?.preventDefault();
        const msg = message.trim();
        if (!msg) return;
        setSubmitting(true);
        try {
            await api.submitFeedback({
                session_id: getSessionId(),
                category,
                message: msg,
                path: window.location.pathname,
            });
            toast.success("Thank you — we read every one");
            setMessage("");
            setOpen(false);
        } catch {
            toast.error("Could not send. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <button
                data-testid="feedback-fab"
                onClick={() => setOpen(true)}
                className="fixed right-0 top-[38%] z-[60] bg-burgundy/90 text-cream h-12 w-8 sm:w-11 rounded-l-full flex items-center justify-center shadow-md hover:bg-burgundy-deep transition-colors"
                aria-label="Report a bug or share feedback"
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center"
                        onClick={() => setOpen(false)}
                        data-testid="feedback-modal"
                    >
                        <motion.div
                            initial={{ y: 30, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 30, opacity: 0 }}
                            onClick={e => e.stopPropagation()}
                            className="w-full sm:max-w-md bg-cream border-t sm:border border-burgundy/20 p-6 pb-safe-sm sm:pb-6"
                        >
                            <div className="text-[10px] tracking-[0.32em] uppercase text-gold-deep">Tell us</div>
                            <h3 className="mt-2 font-display text-2xl text-burgundy">
                                <span className="italic">What's</span> on your mind?
                            </h3>

                            <div className="mt-4 flex gap-1.5" data-testid="feedback-category-group">
                                {CATEGORIES.map(c => (
                                    <button
                                        key={c.value}
                                        data-testid={`feedback-cat-${c.value}`}
                                        type="button"
                                        onClick={() => setCategory(c.value)}
                                        className={`px-4 py-2.5 min-h-[40px] text-[10px] tracking-[0.24em] uppercase border transition-colors ${
                                            category === c.value ? "bg-burgundy text-cream border-burgundy" : "border-burgundy/20 text-ink hover:border-burgundy/40"
                                        }`}
                                    >
                                        {c.label}
                                    </button>
                                ))}
                            </div>

                            <form onSubmit={submit} className="mt-4">
                                <textarea
                                    data-testid="feedback-message"
                                    rows={5}
                                    value={message}
                                    onChange={e => setMessage(e.target.value)}
                                    placeholder="What happened? What would help?"
                                    maxLength={2000}
                                    className="w-full bg-cream border border-burgundy/20 focus:border-burgundy p-3 font-display text-base text-ink focus:outline-none"
                                />
                                <div className="text-[10px] tracking-[0.22em] uppercase text-ink-light text-right">
                                    {message.length} / 2000
                                </div>
                                <div className="mt-3 flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setOpen(false)}
                                        className="flex-1 border border-burgundy/30 text-burgundy uppercase text-[11px] tracking-[0.22em] py-3"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        data-testid="feedback-submit"
                                        type="submit"
                                        disabled={submitting || !message.trim()}
                                        className="flex-1 bg-burgundy text-cream uppercase text-[11px] tracking-[0.22em] py-3 hover:bg-burgundy-deep transition-colors disabled:opacity-50"
                                    >
                                        {submitting ? "Sending…" : "Send"}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
